import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Palette, X } from "lucide-react";
import { useExperience } from "../context/ExperienceContext";
import { MOODS } from "../lib/moods";

const Row = ({ label, options, value, onChange, prefix }) => (
  <div className="mb-5">
    <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-fg/50 mb-2">{label}</p>
    <div className="grid gap-1.5 grid-cols-3">
      {options.map((opt) => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          data-testid={`${prefix}-${opt}`}
          className={`shape-r py-2 font-mono text-[10px] tracking-[0.1em] uppercase transition-colors border ${
            value === opt ? "bg-primary-c text-bg border-transparent" : "border-white/15 text-fg/60 hover:border-white/40"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

export default function ExperiencePanel() {
  const [open, setOpen] = useState(false);
  const { energy, setEnergy, palette, setPalette, shape, setShape } = useExperience();

  return (
    <div className="fixed bottom-5 left-5 z-50">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="glass shape-r p-5 w-72 mb-3 shadow-2xl"
            data-testid="experience-panel"
          >
            <div className="flex items-center justify-between mb-4">
              <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-primary-c">The Emergent Experience</p>
              <button onClick={() => setOpen(false)} data-testid="panel-close" className="text-fg/50 hover:text-fg"><X size={16} /></button>
            </div>

            {/* Mood — color-by-mood picker */}
            <div className="mb-5">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-fg/50 mb-3">Mood</p>
              <div className="grid grid-cols-3 gap-y-4 gap-x-2" data-testid="palette-orbs">
                {MOODS.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setPalette(m.id)}
                    data-testid={`palette-${m.id}`}
                    className="flex flex-col items-center gap-1.5 group"
                    aria-label={`${m.label} mood`}
                  >
                    <span
                      className="h-9 w-9 rounded-full transition-transform group-hover:scale-110"
                      style={{
                        backgroundColor: m.color,
                        boxShadow: palette === m.id ? `0 0 0 2px var(--bg-elev), 0 0 0 4px ${m.color}` : `0 2px 12px ${m.color}55`,
                      }}
                    />
                    <span className={`font-mono text-[9px] tracking-[0.1em] uppercase ${palette === m.id ? "text-fg" : "text-fg/45"}`}>{m.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <Row label="Energy" prefix="energy" options={["calm", "balanced", "electric"]} value={energy} onChange={setEnergy} />
            <Row label="Shape" prefix="shape" options={["sharp", "soft", "round"]} value={shape} onChange={setShape} />
          </motion.div>
        )}
      </AnimatePresence>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="experience-panel-toggle"
        className="glass shape-r w-14 h-14 flex items-center justify-center text-primary-c hover:scale-105 transition-transform"
        aria-label="Change the site color / mood"
      >
        <Palette size={20} />
      </button>
    </div>
  );
}
